import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Form, Input, Button, Header, Icon } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import fetch from '../../lib/hmac-fetch.js';
import { ADMIN_V1_URL } from '../../strings.js';
import './equipment-service-request.css';

const REQUEST_TYPE_OPTIONS = [
  { key: 'technical_support', text: 'Technical Support (errors, broken parts, temp issues)', value: 'technical_support' },
  { key: 'emergency_repair', text: 'Emergency Repair (leaks, flooding, major hazards)', value: 'emergency_repair' },
  { key: 'replenishment', text: 'Replenishments (products, beans, syrups, CO2)', value: 'replenishment' },
  { key: 'cleaning', text: 'Sanitization (deep cleans, line flushing)', value: 'cleaning' },
  { key: 'preventative_maintenance', text: 'Preventative Service (filters, descaling, routine service)', value: 'preventative_maintenance' },
  { key: 'install_move_removal', text: 'Installations & Moves (setup, relocation, upgrades)', value: 'install_move_removal' },
  { key: 'rental_info', text: 'Rental Info (terms, minimum spend, equipment details)', value: 'rental_info' },
  { key: 'other', text: 'Other', value: 'other' },
];

export class EquipmentServiceRequest extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      equipment_id: null,
      request_type: '',
      description: '',
      attachments: [],
      success: false,
      failed: false,
      submitting: false,
    };
  }

  handleChange = (e, { name, value }) => {
    this.setState({ [name]: value });
  };

  handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    this.setState({ attachments: files });
  };

  handleSubmit = () => {
    if (this.state.submitting) return;
    this.setState({ submitting: true });
    const formData = new FormData();
    formData.append('equipment_id', this.state.equipment_id);
    formData.append('request_type', this.state.request_type);
    formData.append('description', this.state.description);

    this.state.attachments.forEach((file) => {
      formData.append('attachments[]', file);
    });

    fetch(
      `${ADMIN_V1_URL}/locations/${this.props.locationId}/equipment/service_requests`,
      {
        method: 'POST',
        body: formData,
      }
    )
      .then((response) => {
        if (response.status !== 201) {
          this.setState({ failed: true, submitting: false });
        } else {
          this.setState({ success: true });
        }
      })
      .catch(() => {
        this.setState({ failed: true, submitting: false });
      });
  };

  render() {
    const { equipment } = this.props;

    if (this.state.success) {
      return (
        <div className="equipment-service-request">
          <hr className="service-request-divider" />
          <h2 className="service-request-heading">Equipment Service Request</h2>
          <div className="service-request-notification">
            <Header.Subheader>
              Thank you! Your service request has been submitted. We'll get back to you shortly.
            </Header.Subheader>
            <Icon className="check" name="check circle" size="huge" />
            <br />
            <Link to="/equipment" className="service-request-back-link">
              Back to Equipment
            </Link>
          </div>
        </div>
      );
    }

    if (this.state.failed) {
      return (
        <div className="equipment-service-request">
          <hr className="service-request-divider" />
          <h2 className="service-request-heading">Equipment Service Request</h2>
          <div className="service-request-notification">
            <Header.Subheader>
              Something went wrong. Please try again later.
            </Header.Subheader>
            <Icon className="warning" name="warning sign" size="huge" />
          </div>
        </div>
      );
    }

    const equipmentOptions = equipment.map((eq) => ({
      key: eq.id,
      text: `${eq.model_name}${eq.location_description ? ' — ' + eq.location_description : ''}${eq.serial_number ? ' - ' + eq.serial_number : ''}`,
      value: eq.id,
    }));

    return (
      <div className="equipment-service-request">
        <Link to="/equipment" className="service-request-back-link top">
          &larr; Back to Equipment
        </Link>
        <hr className="service-request-divider" />
        <h2 className="service-request-heading">Equipment Service Request</h2>
        <p className="service-request-subheading">
          Tell us how we can help with your equipment.
        </p>
        <div className="service-request-form-wrap">
          <Form onSubmit={this.handleSubmit}>
              <Form.Field>
                <label>Equipment</label>
                <Form.Dropdown
                  selection
                  fluid
                  name="equipment_id"
                  value={this.state.equipment_id}
                  onChange={this.handleChange}
                  options={equipmentOptions}
                  placeholder="Select equipment"
                  required
                />
              </Form.Field>
              <Form.Field>
                <label>Request Type</label>
                <Form.Dropdown
                  selection
                  fluid
                  name="request_type"
                  value={this.state.request_type}
                  onChange={this.handleChange}
                  options={REQUEST_TYPE_OPTIONS}
                  placeholder="Select request type"
                  required
                />
              </Form.Field>
              {this.state.request_type === 'emergency_repair' && (
                <p style={{ color: '#d9534f', fontWeight: 600, margin: '0 0 1rem' }}>
                  Please turn off the water supply to the equipment immediately to prevent further damage.
                </p>
              )}
              <Form.Field>
                <label>Description</label>
                <Form.TextArea
                  name="description"
                  value={this.state.description}
                  onChange={this.handleChange}
                  placeholder="Share specific details here"
                  required
                />
              </Form.Field>
              <Form.Field>
                <label>Photos or Videos (encouraged)</label>
                <Input
                  type="file"
                  accept=".jpg,.jpeg,.png,.gif,.webp,.mp4,.mov,.webm,image/jpeg,image/png,image/gif,image/webp,video/mp4,video/quicktime,video/webm"
                  name="attachments"
                  onChange={this.handleFileChange}
                  multiple
                />
              </Form.Field>
              <Button fluid primary type="submit" loading={this.state.submitting} disabled={this.state.submitting}>
                Submit Service Request
              </Button>
            </Form>
        </div>
      </div>
    );
  }
}

EquipmentServiceRequest.propTypes = {
  locationId: PropTypes.number,
  equipment: PropTypes.array.isRequired,
};

function mapStateToProps(state) {
  return {
    locationId: state.locations.open.id,
    equipment: state.equipment.items,
  };
}

function areStatesEqual(prev, next) {
  return (
    prev.locations.open.id === next.locations.open.id &&
    prev.equipment.items === next.equipment.items
  );
}

export default connect(mapStateToProps, null, null, { areStatesEqual })(
  EquipmentServiceRequest
);
